import { uploadVenueImage, VenueImageUploadError } from '@/lib/venue/venueImageUploadApi'
import {
  addUploadingImage,
  failImageUpload,
  generateImageClientKey,
  moveImage,
  removeImage,
  resolveImageUpload,
  VENUE_PROFILE_LIMITS,
  type VenueImageFormItem,
} from '@/lib/venue/venueProfileViewModel'

/**
 * 시설 이미지 목록 편집기(최대 5장, `#867` 설계 §2 — `VenueImageListEditor` 개편).
 *
 * 🚨 `#865` 로 "대표 = 배열 0번째" 불변식이 폐기됐다 — 대표 이미지는 `VenueMainImageField` 가 별도
 * 슬롯으로 관리하므로, 이 컴포넌트는 "대표로 지정" 개념이 없다(순수 시설 사진 목록).
 * 🚨 배열을 직접 조작하지 않고 `venueProfileViewModel.ts` 의 순수 함수만 호출한다 — 페이지가
 * `onChange` 로 폼 상태를 갱신하고 `facilityImagesDirty` 를 함께 켠다.
 * 🚫 재시도 버튼은 두지 않는다 — 실패한 항목은 제거 후 "사진 추가"로 다시 고르면 된다(YAGNI).
 *
 * 🚨 `#879` — 장별 업로드 성공 직후 `onUploaded`로 상위(페이지)에 알려 즉시 서버에 반영시킨다(로컬
 * 상태만 갱신되고 전역 "저장"을 눌러야만 커밋되던 문제 수정). 업로드 실패 항목에는 호출하지 않는다 —
 * 저장할 값이 없다.
 */
export function VenueFacilityImageListEditor({
  venueId,
  images,
  onChange,
  onUploaded,
}: {
  venueId: string
  images: VenueImageFormItem[]
  onChange: (images: VenueImageFormItem[]) => void
  /** 파일 1장 업로드 성공 직후 호출된다 — 페이지가 그 시점의 전체 배열을 즉시 서버에 저장하는 용도(`#879`). */
  onUploaded?: (images: VenueImageFormItem[]) => void | Promise<void>
}) {
  const atLimit = images.length >= VENUE_PROFILE_LIMITS.facilityImagesMax

  async function handleFilesSelected(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return

    const remaining = Math.max(0, VENUE_PROFILE_LIMITS.facilityImagesMax - images.length)
    const files = Array.from(fileList).slice(0, remaining)

    let current = images
    for (const file of files) {
      const clientKey = generateImageClientKey()
      const previewUrl = URL.createObjectURL(file)
      current = addUploadingImage(current, clientKey, previewUrl)
      onChange(current)

      const index = current.length - 1
      let uploadSucceeded = false
      try {
        const result = await uploadVenueImage(file, venueId, index)
        current = resolveImageUpload(current, clientKey, result)
        uploadSucceeded = true
      } catch (error) {
        const message = error instanceof VenueImageUploadError ? error.message : '이미지를 업로드하지 못했습니다.'
        current = failImageUpload(current, clientKey, message)
      }
      onChange(current)

      if (uploadSucceeded) {
        await onUploaded?.(current)
      }
    }
  }

  return (
    <div className="space-y-3">
      {images.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {images.map((item, index) => (
            <div
              key={item.clientKey}
              className="relative aspect-square overflow-hidden rounded-lg border border-slateish-200 bg-slateish-50"
            >
              <img src={item.previewUrl} alt={`시설 사진 ${index + 1}`} className="h-full w-full object-cover" />

              {item.status === 'uploading' ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-xs text-white">
                  업로드 중…
                </div>
              ) : null}

              {item.status === 'error' ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/60 p-1 text-center text-[11px] text-white">
                  <span>{item.errorMessage ?? '업로드 실패'}</span>
                  <button
                    type="button"
                    className="underline"
                    onClick={() => {
                      onChange(removeImage(images, item.clientKey))
                    }}
                  >
                    제거
                  </button>
                </div>
              ) : null}

              {item.status === 'done' ? (
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/50 px-1 py-0.5">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      aria-label={`${index + 1}번째 사진 위로 이동`}
                      disabled={index === 0}
                      className="rounded px-1 text-xs text-white disabled:opacity-30"
                      onClick={() => {
                        onChange(moveImage(images, item.clientKey, 'up'))
                      }}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`${index + 1}번째 사진 아래로 이동`}
                      disabled={index === images.length - 1}
                      className="rounded px-1 text-xs text-white disabled:opacity-30"
                      onClick={() => {
                        onChange(moveImage(images, item.clientKey, 'down'))
                      }}
                    >
                      ↓
                    </button>
                  </div>
                  <button
                    type="button"
                    aria-label={`${index + 1}번째 사진 삭제`}
                    className="rounded px-1 text-xs text-white"
                    onClick={() => {
                      onChange(removeImage(images, item.clientKey))
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-facility-image-input">
          시설 사진 추가
        </label>
        <input
          id="venue-facility-image-input"
          type="file"
          accept="image/*"
          multiple
          disabled={atLimit}
          className="block text-sm text-ink-700"
          onChange={(event) => {
            void handleFilesSelected(event.target.files)
            event.target.value = ''
          }}
        />
        <p className="mt-1 text-xs text-slateish-500">
          최대 {VENUE_PROFILE_LIMITS.facilityImagesMax}장까지 추가할 수 있습니다.
        </p>
      </div>
    </div>
  )
}
